/**
 * @typedef {Object} ComboBoxOption
 * @property {string} label - Display label.
 * @property {string} value - Canonical value (case-sensitive).
 * @property {string} [icon] - Optional icon identifier.
 */

import { COMBOBOX_MODE, COMMIT_TRIGGER } from './comboBox.constants';

/**
 * Determines the effective ComboBox mode based on props.
 *
 * @param {Object} params
 * @param {boolean} params.multiple
 * @param {boolean} params.freeSolo
 * @returns {string} One of COMBOBOX_MODE values
 */
export function getComboBoxMode({ multiple, freeSolo }) {
  return multiple
    ? freeSolo
      ? COMBOBOX_MODE.MULTIPLE_FREE_SOLO
      : COMBOBOX_MODE.MULTIPLE_OPTIONS
    : freeSolo
      ? COMBOBOX_MODE.SINGLE_FREE_SOLO
      : COMBOBOX_MODE.SINGLE_OPTIONS;
}

/**
 * Maps RHF field value → MUI Autocomplete value.
 *
 * Rules:
 * - single + options → option | null
 * - single + freeSolo → string
 * - multiple + options → ComboBoxOption[]
 * - multiple + freeSolo → string[]
 *
 * @param {Object} params
 * @param {string|string[]} params.fieldValue
 * @param {ComboBoxOption[]} params.options
 * @param {string} params.mode
 * @returns {any} Value suitable for MUI Autocomplete
 */
export function toAutocompleteValue({ fieldValue, options = [], mode }) {
  const isSingleValue = typeof fieldValue === 'string';
  const isMultipleValue = Array.isArray(fieldValue);

  switch (mode) {
    case COMBOBOX_MODE.SINGLE_OPTIONS:
      if (!isSingleValue) return null;
      return options.find((opt) => opt.value === fieldValue) ?? null;

    case COMBOBOX_MODE.SINGLE_FREE_SOLO:
      if (!isSingleValue) return '';
      return matchOption(fieldValue, options);

    case COMBOBOX_MODE.MULTIPLE_OPTIONS:
      if (!isMultipleValue) return [];
      return options.filter((opt) => fieldValue.includes(opt.value));

    case COMBOBOX_MODE.MULTIPLE_FREE_SOLO:
      if (!isMultipleValue) return [];
      return fieldValue.map((value) => matchOption(value, options));

    default:
      return null;
  }
}

/**
 * Hands a stored value back as the option object it came from, when there is one.
 *
 * FreeSolo values are arbitrary strings, so a value with no matching option stays a
 * string — both shapes are fine for Autocomplete, and `getOptionLabel` reads either.
 * Returning the option lets MUI mark it as selected in the dropdown; a bare string
 * never matches an object option, so the list showed nothing as chosen.
 *
 * @param {string} value
 * @param {ComboBoxOption[]} options
 * @returns {string|ComboBoxOption}
 */
function matchOption(value, options = []) {
  if (typeof value !== 'string') return value;
  return options.find((opt) => opt && typeof opt === 'object' && opt.value === value) ?? value;
}

/**
 * Maps MUI Autocomplete value → RHF field value.
 *
 * Rules:
 * - Options always resolve to option.value
 * - FreeSolo values are used as-is
 *
 * @param {Object} params
 * @param {any} params.autocompleteValue
 * @param {string} params.mode
 * @returns {string|string[]}
 */
export function toFieldValue({ autocompleteValue, mode }) {
  const isValidOption = autocompleteValue && typeof autocompleteValue === 'object';
  const isMultipleValue = Array.isArray(autocompleteValue);

  switch (mode) {
    case COMBOBOX_MODE.SINGLE_OPTIONS:
      if (!isValidOption) return '';
      return autocompleteValue.value ?? '';

    case COMBOBOX_MODE.SINGLE_FREE_SOLO:
      return toStringValue(autocompleteValue) ?? '';

    case COMBOBOX_MODE.MULTIPLE_OPTIONS:
      if (!isMultipleValue) return [];
      return autocompleteValue.map((opt) => opt?.value).filter((v) => typeof v === 'string');

    case COMBOBOX_MODE.MULTIPLE_FREE_SOLO:
      if (!isMultipleValue) return [];
      return autocompleteValue.map(toStringValue).filter((v) => typeof v === 'string');

    default:
      return '';
  }
}

/**
 * Normalizes one Autocomplete entry to the string the form stores.
 *
 * A freeSolo Autocomplete hands back whatever it was given: a typed string, or the
 * option itself when the user picks one off the list. The free-solo branches used to
 * keep only `typeof v === 'string'`, so picking an option out of the dropdown was
 * silently discarded — the value never reached the form and no chip appeared. That
 * only stayed hidden because the two freeSolo fields that existed passed plain string
 * options; the first `{ label, value }` options dropped every selection.
 *
 * @param {string|ComboBoxOption} entry
 * @returns {string|undefined} The stored value, or undefined if it is neither shape.
 */
function toStringValue(entry) {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object' && typeof entry.value === 'string') return entry.value;
  return undefined;
}

/**
 * Checks whether a value already exists.
 * Case-sensitive comparison (===).
 *
 * @param {Object} params
 * @param {string|string[]} params.currentValue
 * @param {string} params.nextValue
 * @returns {boolean}
 */
export function isDuplicateValue({ currentValue, nextValue }) {
  if (typeof nextValue !== 'string') return false;

  if (Array.isArray(currentValue)) {
    return currentValue.includes(nextValue);
  }

  if (typeof currentValue === 'string') {
    return currentValue === nextValue;
  }

  return false;
}

/**
 * Determines whether freeSolo input should be committed.
 *
 * Rules:
 * - Highlighted options are committed ONLY via explicit selection (click / enter)
 * - Blur always commits typed text
 * - Space commits only if commitOnSpace === true
 *
 * @param {Object} params
 * @param {string} params.trigger - One of COMMIT_TRIGGER
 * @param {boolean} params.hasHighlightedOption
 * @param {boolean} params.commitOnSpace
 * @returns {boolean}
 */
export function shouldCommitFreeSolo({ trigger, hasHighlightedOption, commitOnSpace }) {
  switch (trigger) {
    case COMMIT_TRIGGER.BLUR:
      return true;

    case COMMIT_TRIGGER.ENTER:
      return !hasHighlightedOption;

    case COMMIT_TRIGGER.SPACE:
      return Boolean(commitOnSpace);

    default:
      return false;
  }
}

/**
 * Commits freeSolo input into the current value.
 *
 * Rules:
 * - No duplicates (case-sensitive)
 * - Empty or whitespace-only input is ignored
 *
 * @param {Object} params
 * @param {string} params.inputValue
 * @param {string|string[]} params.currentValue
 * @param {string} params.mode
 * @returns {string|string[]|null} Updated value or null if no change
 */
export function commitFreeSoloValue({ inputValue, currentValue, mode }) {
  if (typeof inputValue !== 'string') return null;

  const trimmed = inputValue.trim();
  if (!trimmed) return null;

  switch (mode) {
    case COMBOBOX_MODE.SINGLE_FREE_SOLO:
      if (currentValue === trimmed) return null;
      return trimmed;

    case COMBOBOX_MODE.MULTIPLE_FREE_SOLO: {
      const existing = Array.isArray(currentValue) ? currentValue : [];
      if (isDuplicateValue({ currentValue: existing, nextValue: trimmed })) return null;
      return [...existing, trimmed];
    }

    default:
      return null;
  }
}

/**
 * Builds the chip display model for rendering.
 *
 * @param {Object} params
 * @param {string[]} params.values - RHF field values
 * @param {ComboBoxOption[]} params.options
 * @param {string} params.mode
 * @returns {Array<{ label: string, value: string, icon?: string }>}
 */
export function buildChipModel({ values, options, mode }) {
  if (!Array.isArray(values)) return [];

  switch (mode) {
    case COMBOBOX_MODE.MULTIPLE_OPTIONS: {
      const optionMap = new Map(options.map((opt) => [opt.value, opt]));

      return values.map((value) => optionMap.get(value)).filter(Boolean);
    }

    // A freeSolo value may be typed text or a picked option; show the option's label
    // when there is one so the chip does not fall back to a raw code.
    case COMBOBOX_MODE.MULTIPLE_FREE_SOLO:
      return values.map((value) => {
        const matched = matchOption(value, options);
        return typeof matched === 'string' ? { label: value, value } : matched;
      });

    default:
      return [];
  }
}
